import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Award, Users, Clock, Shield, ArrowRight, Check,
  Zap, Target, Wrench, Lightbulb, Phone, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

export default function About() {
  const { language } = useLanguage();
  const isEs = language === 'es';

  const values = [
    {
      icon: Target,
      title: isEs ? 'Excelencia' : 'Excellence',
      description: isEs 
        ? 'Buscamos la excelencia en cada proyecto, sin importar el tamaño.' 
        : 'We pursue excellence in every project, no matter the size.'
    },
    {
      icon: Shield,
      title: isEs ? 'Integridad' : 'Integrity',
      description: isEs 
        ? 'Comunicación honesta y precios transparentes siempre.' 
        : 'Honest communication and transparent pricing always.'
    },
    {
      icon: Lightbulb,
      title: isEs ? 'Innovación' : 'Innovation',
      description: isEs 
        ? 'Manteniéndonos a la vanguardia con la última tecnología.' 
        : 'Staying ahead with the latest technology and methods.'
    },
    {
      icon: Wrench,
      title: isEs ? 'Confiabilidad' : 'Reliability',
      description: isEs 
        ? 'Servicio confiable en el que puede confiar, siempre.' 
        : 'Dependable service you can count on, every time.'
    }
  ];

  const stats = [
    { value: '13+', label: isEs ? 'Años de Experiencia' : 'Years Experience', icon: Clock },
    { value: '280+', label: isEs ? 'Proyectos Completados' : 'Projects Completed', icon: Award },
    { value: '200+', label: isEs ? 'Clientes Satisfechos' : 'Happy Clients', icon: Users },
    { value: '50+', label: isEs ? 'Técnicos Certificados' : 'Certified Technicians', icon: Shield },
  ];

  const certifications = [
    'BICSI Certified Installer',
    'Fiber Optic Association Certified',
    'OSHA Safety Certified',
    'Manufacturer Certified Partner',
    'State Licensed Contractor',
    'BBB Accredited Business'
  ];

  return (
    <div className="min-h-screen bg-gray-100" data-testid="about-page">
      {/* Hero Section - Same style as SEO pages */}
      <section className="relative bg-gradient-to-br from-[#0a1628] via-[#1a2d4a] to-[#0f2341] text-white pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">{isEs ? 'Sirviendo toda Florida' : 'Serving all of Florida'}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {isEs ? 'Acerca de ' : 'About '}<span className="text-orange-500">IQES</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              {isEs 
                ? 'Su socio de confianza en soluciones de bajo voltaje'
                : 'Your trusted partner in low voltage solutions'}
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/quote">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-full">
                  {isEs ? 'Obtener Cotización Gratis' : 'Get Free Quote'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:7869813760">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full">
                  <Phone className="mr-2 w-5 h-5" />
                  786-981-3760
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-orange-500" />
                </div>
                <span className="text-orange-500 font-semibold text-sm">IQES</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                {isEs ? 'Nuestra Misión' : 'Our Mission'}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {isEs 
                  ? 'Potenciando la infraestructura de Florida con soluciones de bajo voltaje de próxima generación.'
                  : 'Empowering Florida\'s infrastructure with next-generation low voltage solutions.'}
              </p>
              <p className="text-gray-600 mb-6">
                {isEs 
                  ? 'Fundada con la visión de revolucionar la infraestructura de bajo voltaje en Florida, IQES se ha convertido en un socio de confianza para empresas en todo el estado. Nuestro equipo de profesionales certificados combina experiencia técnica con un compromiso con la satisfacción del cliente que nos distingue.'
                  : 'Founded with a vision to revolutionize low voltage infrastructure in Florida, IQES has grown to become a trusted partner for businesses across the state. Our team of certified professionals combines technical expertise with a commitment to customer satisfaction that sets us apart.'}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">{isEs ? 'Licenciado y Asegurado' : 'Licensed & Insured'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">{isEs ? 'Cotizaciones Gratis' : 'Free Estimates'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">{isEs ? 'Servicio 24/7' : '24/7 Support'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">{isEs ? 'Trabajo Garantizado' : 'Guaranteed Work'}</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwZGF0YSUyMGNlbnRlciUyMGJsdWUlMjBsaWdodHMlMjBtb2Rlcm58ZW58MHx8fHwxNzcwNzM4NDI0fDA&ixlib=rb-4.1.0&q=85"
                  alt="IQES Data Center"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#003366]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-4">
                  <stat.icon className="w-8 h-8 text-orange-500" />
                </div>
                <div className="text-4xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-300">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {isEs ? 'Nuestros Valores' : 'Our Core Values'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {isEs ? 'Los principios que guían todo lo que hacemos' : 'The principles that guide everything we do'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow"
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                  <value.icon className="w-7 h-7 text-orange-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {isEs ? 'Certificaciones y Asociaciones' : 'Certifications & Partnerships'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {isEs ? 'Credenciales reconocidas que garantizan calidad' : 'Industry-recognized credentials that guarantee quality'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {certifications.map((cert, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 border border-gray-200"
              >
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-green-500" />
                </div>
                <span className="text-gray-800 font-medium">{cert}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#003366]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {isEs ? '¿Listo para Trabajar con Nosotros?' : 'Ready to Work With Us?'}
            </h2>
            <p className="text-gray-300 mb-8">
              {isEs 
                ? 'Discutamos su proyecto y veamos cómo IQES puede ayudar a transformar su infraestructura.'
                : 'Let\'s discuss your project and see how IQES can help transform your infrastructure.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/quote" data-testid="about-quote-btn">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-full">
                  {isEs ? 'Obtener Cotización' : 'Get Quote'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="about-contact-btn">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full">
                  {isEs ? 'Contáctenos' : 'Contact Us'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
